import React, { Fragment, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import PageTitle from "../layouts/PageTitle";
import { Fn_AddEditData, Fn_FillListData } from "../../store/Functions";
import { useDispatch } from "react-redux";
import { API_WEB_URLS } from "../../constants/constAPI";
import { toast } from "react-toastify";
import useSession from "../utils/useSession";

const AddEdit_FirmShiftTimeMapping = () => {
  const { firmId: sessionFirmId } = useSession();
  const [selectedFirm, setSelectedFirm] = useState(() => sessionFirmId ? String(sessionFirmId) : "");
  const [firms, setFirms] = useState([]);
  const [branches, setBranches] = useState([]);
  const [rows, setRows] = useState([{ Id: 0, F_BranchMaster: "", InTime: "", OutTime: "" }]);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Keep selectedFirm synced with navbar session
  useEffect(() => {
    if (sessionFirmId) {
      setSelectedFirm(String(sessionFirmId));
    }
  }, [sessionFirmId]);

  // Load firms on mount
  useEffect(() => {
    Fn_FillListData(dispatch, setFirms, "gridData", `${API_WEB_URLS.MASTER}/0/token/FirmMaster/Id/0`);
  }, [dispatch]);

  // Load branches and existing mappings when firm changes
  const loadData = async () => {
    if (!selectedFirm) {
      setBranches([]);
      setRows([{ Id: 0, F_BranchMaster: "", InTime: "", OutTime: "" }]);
      return;
    }

    // 1. Fetch branches of selected firm
    const branchRes = await Fn_FillListData(dispatch, () => {}, "none", `${API_WEB_URLS.MASTER}/0/token/GetBranchByFirmId/Id/${selectedFirm}`);
    setBranches(branchRes || []);

    // 2. Fetch existing shift mappings from FirmShiftTimeData/Id/0
    try {
      const mappingRes = await Fn_FillListData(dispatch, () => {}, "none", `${API_WEB_URLS.MASTER}/0/token/FirmShiftTimeData/Id/0`);
      let parsedMappings = [];
      if (mappingRes && Array.isArray(mappingRes) && mappingRes.length > 0) {
        const jsonStr = mappingRes[0]?.FirmShiftTimeJson;
        if (jsonStr) {
          try {
            parsedMappings = typeof jsonStr === "string" ? JSON.parse(jsonStr) : jsonStr;
          } catch (err) {
            console.error("Error parsing FirmShiftTimeJson:", err);
            parsedMappings = [];
          }
        } else if (Array.isArray(mappingRes[0])) {
          parsedMappings = mappingRes[0];
        } else if (mappingRes[0]?.F_FirmMaster) {
          parsedMappings = mappingRes;
        }
      }

      // Filter mappings for the currently selected firm
      const firmMappings = (parsedMappings || []).filter(
        (m) => String(m.F_FirmMaster) === String(selectedFirm)
      );

      if (firmMappings.length > 0) {
        setRows(
          firmMappings.map((m) => ({
            Id: m.Id || 0,
            F_BranchMaster: String(m.F_BranchMaster || ""),
            InTime: m.InTime || "",
            OutTime: m.OutTime || "",
          }))
        );
      } else {
        setRows([{ Id: 0, F_BranchMaster: "", InTime: "", OutTime: "" }]);
      }
    } catch (err) {
      console.error("Error loading FirmShiftTimeData:", err);
      setRows([{ Id: 0, F_BranchMaster: "", InTime: "", OutTime: "" }]);
    }
  };

  useEffect(() => {
    loadData();
  }, [selectedFirm, dispatch]);

  const addRow = () => {
    const nextIdx = rows.length;
    setRows((prev) => [...prev, { Id: 0, F_BranchMaster: "", InTime: "", OutTime: "" }]);
    setTimeout(() => {
      const select = document.getElementById(`branch-select-${nextIdx}`);
      if (select) select.focus();
    }, 100);
  };

  const removeRow = (index) => {
    if (rows.length === 1) return;
    setRows((prev) => prev.filter((_, i) => i !== index));
  };

  const handleRowChange = (index, field, value) => {
    setRows((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row))
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedFirm) {
      alert("Please select a Firm first.");
      return;
    }

    // Validate that branches, in time and out time are selected
    const invalidRow = rows.find(r => !r.F_BranchMaster || !r.InTime || !r.OutTime);
    if (invalidRow) {
      alert("Please make sure all rows have Branch, In Time and Out Time configured.");
      return;
    }

    // Check for duplicate branch shift mappings in rows
    const combinations = rows.map(r => `${r.F_BranchMaster}_${r.InTime}_${r.OutTime}`);
    if (new Set(combinations).size !== combinations.length) {
      alert("Duplicate branch shift timings detected in rows. Please make sure each row has a unique combination of branch and shift times.");
      return;
    }

    try {
      // Map back to the required format
      const payload = rows.map(r => ({
        Id: String(r.Id || 0),
        F_FirmMaster: String(selectedFirm),
        F_BranchMaster: String(r.F_BranchMaster),
        InTime: r.InTime,
        OutTime: r.OutTime
      }));

      const formData = new FormData();
      formData.append("FirmShiftTimeJson", JSON.stringify(payload));

      await Fn_AddEditData(
        dispatch,
        () => {}, // no local state loading update needed
        { arguList: { id: 0, formData } },
        'FirmShiftTimeMapping/0/token',
        true,
        "memberid",
        navigate,
        "#" // Stay on page or redirect to dashboard/some other url
      );
      toast.success("Firm Shift Time Mappings saved successfully!");
      loadData();
    } catch (error) {
      console.error("Error saving mappings:", error);
      alert("An error occurred while saving mappings. Please try again.");
    }
  };

  return (
    <Fragment>
      <PageTitle activeMenu="Masters" motherMenu="Masters" pageContent="Firm Shift Time Mapping" />

      <div className="row">
        <div className="col-lg-12">
          <div className="card">
            <div className="card-header">
              <h4 className="card-title">Firm Shift Time Mapping</h4>
            </div>
            <div className="card-body">
              <form onSubmit={handleSubmit}>
                <div className="row mb-4">
                  <div className="col-md-6">
                    <label className="text-label font-w600 fs-16">Firm Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={firms.find((f) => String(f.Id) === String(selectedFirm))?.Name || "Please select firm from dashboard"}
                      readOnly
                      disabled
                    />
                  </div>
                </div>

                {selectedFirm && (
                  <div className="row">
                    <div className="col-md-12">
                      <div className="table-responsive">
                        <table className="table table-bordered">
                          <thead>
                            <tr>
                              <th>Branch *</th>
                              <th>In Time *</th>
                              <th>Out Time *</th>
                              <th style={{ width: "100px", textAlign: "center" }}>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {rows.map((row, idx) => (
                              <tr key={idx}>
                                <td>
                                  <select
                                    id={`branch-select-${idx}`}
                                    className="form-control"
                                    value={row.F_BranchMaster}
                                    onChange={(e) => handleRowChange(idx, "F_BranchMaster", e.target.value)}
                                    required
                                  >
                                    <option value="">-- Select Branch --</option>
                                    {branches.map((b) => (
                                      <option key={b.Id} value={b.Id}>
                                        {b.Name}
                                      </option>
                                    ))}
                                  </select>
                                </td>
                                <td>
                                  <input
                                    type="time"
                                    className="form-control"
                                    value={row.InTime}
                                    onChange={(e) => handleRowChange(idx, "InTime", e.target.value)}
                                    required
                                  />
                                </td>
                                <td>
                                  <input
                                    type="time"
                                    className="form-control"
                                    value={row.OutTime}
                                    onChange={(e) => handleRowChange(idx, "OutTime", e.target.value)}
                                    required
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter") {
                                        e.preventDefault();
                                        const plusBtn = document.getElementById(`plus-btn-${idx}`);
                                        if (plusBtn) plusBtn.focus();
                                      }
                                    }}
                                  />
                                </td>
                                <td className="text-center" style={{ verticalAlign: "middle" }}>
                                  <div className="d-flex justify-content-center gap-2">
                                    <button
                                      id={`plus-btn-${idx}`}
                                      type="button"
                                      className="btn btn-success btn-xs p-2"
                                      onClick={addRow}
                                      style={{ display: "flex", alignItems: "center", justifyContent: "center", minWidth: "24px", height: "24px" }}
                                      onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                          e.preventDefault();
                                          addRow();
                                        } else if (e.key === "Tab" && !e.shiftKey) {
                                          e.preventDefault();
                                          const saveBtn = document.getElementById("save-mappings-btn");
                                          if (saveBtn) saveBtn.focus();
                                        }
                                      }}
                                    >
                                      <i className="fa fa-plus"></i>
                                    </button>
                                    <button
                                      type="button"
                                      className="btn btn-danger btn-xs p-2"
                                      onClick={() => removeRow(idx)}
                                      disabled={rows.length === 1}
                                      style={{ display: "flex", alignItems: "center", justifyContent: "center", minWidth: "24px", height: "24px" }}
                                    >
                                      <i className="fa fa-minus"></i>
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      <div className="mt-4">
                        <button id="save-mappings-btn" type="submit" className="btn btn-primary me-2">
                          Save Mappings
                        </button>
                        <button
                          type="button"
                          className="btn btn-light"
                          onClick={() => navigate("/FirmMaster")}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default AddEdit_FirmShiftTimeMapping;
